import * as PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as ModalContainer from 'ui-library/ModalContainer';

interface ModalAnchorProps {
  children?: React.ReactNode;
  open: boolean;
}

interface State {
  modalKey: string;
}

/**
 * ModalAnchor is re-rendered asynchronously via debounce (via ModalContainer.open), so storing state in the
 * parent component and threading it as a prop to a child of ModalAnchor will not render immediately, which
 * can cause odd rendering bugs, e.g. the cursor moving to the end of the input when trying to modify a
 * character in the middle of an input.
 *
 * To avoid this:
 *  - the child (Modal) of ModalAnchor should be its own component with its own controlled state
 *  - the parent component should compose ModalAnchor with the Modal component, threading only "static" props
 *
 * Example:
 *   function MyTextModal(props) {
 *      const [val, setVal] = useState('');
 *      return (
 *        <Modal {...props} >
 *          <input type="text" value={val} onChange={(e) => { setVal(e.target.value); }} />
 *        </Modal>
 *      );
 *   }
 *
 *   function Parent() {
 *     const [open, setOpen] = useState(false);
 *     return (
 *       <>
 *         <button onClick={() => { setOpen(true); }}>Open</button>
 *         <ModalAnchor open={open}>
 *           <MyTextModal title="Hello" onRequestClose={() => { setOpen(false); }}/>
 *         </ModalAnchor>
 *       </>
 *     );
 *   }
 */
export default class ModalAnchor extends React.Component<ModalAnchorProps, State> {
  static propTypes = {
    // Would like to check child is bs.Modal, but rendering a bs.Modal is not the same as being a subclass.
    children: (props: ModalAnchorProps, ...args: [string, string, string, string]) => {
      if (props.open) {
        return PropTypes.node.isRequired(props, ...args);
      } else {
        return PropTypes.node(props, ...args);
      }
    },
    open: PropTypes.bool.isRequired,
  };

  state: State = {
    modalKey: _.uniqueId('modal-anchor-'),
  };

  componentDidMount(): void {
    if (this.props.open) {
      this._open();
    }
  }

  componentDidUpdate(prevProps: ModalAnchorProps): void {
    // Call this._open if:
    //  - props.open change to change the modal open/close state
    //  - any other props change in case the modal relies on the parent to pass along new props to the modal
    if (!isEqual(this.props, prevProps)) {
      if (this.props.open) {
        this._open();
      } else {
        this._close();
      }
    }
  }

  componentWillUnmount(): void {
    // Always close on unmount.
    this._close();
  }

  _open(): ModalContainer.Modal | null {
    return ModalContainer.open(this.state.modalKey, this.props.children);
  }

  _close(): void {
    ModalContainer.close(this.state.modalKey);
  }

  render(): JSX.Element | null {
    return null;
  }
}
